import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/vendas/pedido-form-page.tsx — Fase 12, Plano 02 (PV-01, D-37, A-02).
// Formulário "Novo pedido do cliente": Cabeçalho + Itens (Sheet lateral) + rodapé fixo.
// Cria o PV por POST /api/v1/vendas/pedidos (apiMutate: sessão por cookie + CSRF).
import { useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import type {
  ClienteLookupPv,
  CriarPedidoVendaInput,
  CriarPedidoVendaItemInput,
  PrioridadePv,
  RascunhoPvDaNota,
} from '@pieltec/contracts';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { obterSessaoAtual } from '../../lib/session';
import { formatarData, formatarMoeda, formatarQuantidade, hojeIso } from '../../lib/vendas/formatacao';
import { pendenciasDoRascunho } from '../../lib/vendas/preco-item';
import { COPIA_NOTA_SEM_ITENS, copiaClienteNaoEncontrado, itemFormDoRascunho } from '../../lib/vendas/pv-da-nota';
import { focarCampo, primeiroCampoInvalido, validarCabecalhoPv } from '../../lib/vendas/validacao-pv';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { PendenciasBadges } from './detalhe/pendencias-badges';
import { PedidoItemSheet, type ItemPvForm } from './pedido-item-sheet';
import { AnexarPvCard, DESTINO_NOVO_PV, destinoInicialAnexar } from './form/anexar-pv-card';
import { MENSAGEM_FALHA_PV, usePvComandos } from './use-pv-comandos';
import { CampoSelect } from '../../components/ui/campo-select';

// O router escuta `popstate`; despachar o evento é a navegação programática sem reload
// (mesmo padrão de novo-cliente-page.tsx).
function navegar(destino: string): void {
  window.history.pushState(null, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const PRIORIDADES: ReadonlyArray<{ valor: PrioridadePv; rotulo: string }> = [
  { valor: 0, rotulo: 'Normal' },
  { valor: 10, rotulo: 'Alta' },
  { valor: 20, rotulo: 'Urgente' },
];

interface ErrosForm {
  clienteId?: string;
  numeroPedidoCliente?: string;
  data?: string;
  responsavelNome?: string;
  condicaoComercial?: string;
  observacoes?: string;
  itens?: string;
}

/** Ordem visual dos campos do formulário e o id do elemento que recebe o foco quando inválido. */
const ORDEM_CAMPOS: ReadonlyArray<{ campo: keyof ErrosForm; idDom: string }> = [
  { campo: 'clienteId', idDom: 'pv-cliente' },
  { campo: 'numeroPedidoCliente', idDom: 'pv-numero-cliente' },
  { campo: 'data', idDom: 'pv-data' },
  { campo: 'responsavelNome', idDom: 'pv-responsavel' },
  { campo: 'condicaoComercial', idDom: 'pv-condicao' },
  { campo: 'observacoes', idDom: 'pv-observacoes' },
  { campo: 'itens', idDom: 'pv-adicionar-item' },
];

/** Remove os campos só de tela (`material`, `precoTabela`): o servidor rejeita propriedades fora do contrato. */
function paraItemDeCriacao({ material: _material, precoTabela: _precoTabela, ...item }: ItemPvForm): CriarPedidoVendaItemInput {
  return item;
}

type CarregamentoNota = 'sem-nota' | 'carregando' | 'ok' | 'erro';

const MENSAGEM_FALHA_NOTA = 'Não foi possível carregar os itens da nota. Volte à conferência e tente novamente.';

export function PedidoFormPage() {
  const { criarPedido, anexarItens } = usePvComandos();
  // Modo `?nota={id}` (PV-04): itens pré-preenchidos da NF para revisão; nada é criado sem o clique em "Criar pedido" (D-03).
  const notaId = useMemo(() => new URLSearchParams(window.location.search).get('nota'), []);
  const [rascunhoNota, setRascunhoNota] = useState<RascunhoPvDaNota | null>(null);
  const [carregamentoNota, setCarregamentoNota] = useState<CarregamentoNota>(notaId ? 'carregando' : 'sem-nota');
  const [destinoAnexo, setDestinoAnexo] = useState<string>(DESTINO_NOVO_PV);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [numeroPedidoCliente, setNumeroPedidoCliente] = useState('');
  const [data, setData] = useState(hojeIso);
  const [prazo, setPrazo] = useState('');
  const [prioridade, setPrioridade] = useState<PrioridadePv>(0);
  const [responsavel, setResponsavel] = useState('');
  const [condicao, setCondicao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<ItemPvForm[]>([]);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [indiceEdicao, setIndiceEdicao] = useState<number | null>(null);
  const [erros, setErros] = useState<ErrosForm>({});
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState<string | null>(null);

  // A-02: "Responsável" é texto livre pré-preenchido com o nome do usuário da sessão.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) {
        setResponsavel((atual) => atual || sessao.usuario.displayName);
      }
    });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    if (!notaId) return;
    let vivo = true;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/vendas/pedidos/rascunho-da-nota/${encodeURIComponent(notaId)}`, { credentials: 'include' });
        if (!vivo) return;
        if (!res.ok) {
          setCarregamentoNota('erro');
          return;
        }
        const rascunho = (await res.json()) as RascunhoPvDaNota;
        if (!vivo) return;
        setRascunhoNota(rascunho);
        setCliente(rascunho.cliente);
        setDestinoAnexo(destinoInicialAnexar(rascunho.pvsAbertos));
        setItens(rascunho.itens.map(itemFormDoRascunho));
        setCarregamentoNota('ok');
      } catch {
        if (vivo) setCarregamentoNota('erro');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [notaId]);

  function destinoAoConcluir(pvId: string): string {
    return notaId ? `/remessas?nota=${encodeURIComponent(notaId)}` : `/vendas/pedidos/detalhe?id=${encodeURIComponent(pvId)}`;
  }

  function abrirNovoItem(): void {
    setIndiceEdicao(null);
    setSheetAberto(true);
  }

  function editarItem(indice: number): void {
    setIndiceEdicao(indice);
    setSheetAberto(true);
  }

  function salvarItem(item: ItemPvForm, continuar: boolean): void {
    setItens((atuais) =>
      indiceEdicao === null
        ? [...atuais, item]
        : atuais.map((existente, i) =>
            // O Sheet não conhece o item fiscal: a edição preserva o vínculo com a NF (T-12-32 valida no servidor).
            i === indiceEdicao ? { ...item, documentoFiscalItemId: existente.documentoFiscalItemId ?? null } : existente,
          ),
    );
    setErros((atuais) => ({ ...atuais, itens: undefined }));
    if (!continuar) setSheetAberto(false);
  }

  function validar(): boolean {
    const proximos: ErrosForm = validarCabecalhoPv({
      clienteId: cliente?.id,
      data,
      numeroPedidoCliente,
      responsavelNome: responsavel,
      condicaoComercial: condicao,
      observacoes,
    });
    if (itens.length === 0) proximos.itens = 'Inclua ao menos um item no pedido.';
    setErros(proximos);
    const invalido = primeiroCampoInvalido(
      proximos,
      ORDEM_CAMPOS.map((c) => c.campo),
    );
    if (invalido) focarCampo(ORDEM_CAMPOS.find((c) => c.campo === invalido)?.idDom);
    return invalido === null;
  }

  // Card "Anexar a PV em aberto": só vale para o cliente resolvido pela nota e com um PV escolhido.
  const pvsAbertosDaNota = rascunhoNota && rascunhoNota.temItens && cliente && cliente.id === rascunhoNota.cliente?.id ? rascunhoNota.pvsAbertos : [];
  const anexando = pvsAbertosDaNota.length > 0 && destinoAnexo !== DESTINO_NOVO_PV && pvsAbertosDaNota.some((pv) => pv.id === destinoAnexo);

  async function anexar(): Promise<void> {
    if (itens.length === 0) {
      setErros({ itens: 'Inclua ao menos um item no pedido.' });
      focarCampo('pv-adicionar-item');
      return;
    }
    setErros({});
    setEnviando(true);
    try {
      // WR-D1-02: a versão é a do PV quando o rascunho da nota foi lido (não a de agora), senão o lock otimista não protege
      // contra alteração/cancelamento do PV entre a decisão do operador e o clique.
      const versao = pvsAbertosDaNota.find((pv) => pv.id === destinoAnexo)?.versao;
      if (versao === undefined) {
        setErroEnvio(MENSAGEM_FALHA_PV);
        return;
      }
      const resultado = await anexarItens(destinoAnexo, {
        versao,
        ...(notaId ? { documentoFiscalId: notaId } : {}),
        itens: itens.map(paraItemDeCriacao),
      });
      if (resultado.tipo === 'conflito') {
        setErroEnvio('O PV mudou depois que a conferência foi aberta. Reabra a conferência da nota (recarregue esta página) e confirme de novo.');
        return;
      }
      if (resultado.tipo !== 'ok') {
        setErroEnvio(resultado.message);
        return;
      }
      setSucesso(`Itens anexados ao PV ${resultado.resultado.numero}.`);
      navegar(destinoAoConcluir(resultado.resultado.id));
    } finally {
      setEnviando(false);
    }
  }

  async function confirmar(): Promise<void> {
    setErroEnvio(null);
    if (anexando) {
      await anexar();
      return;
    }
    if (!validar() || !cliente) return;
    const corpo: CriarPedidoVendaInput = {
      clienteId: cliente.id,
      numeroPedidoCliente: numeroPedidoCliente.trim() || null,
      data,
      prazoSolicitado: prazo || null,
      prioridade,
      responsavelNome: responsavel.trim() || null,
      condicaoComercial: condicao.trim() || null,
      observacoes: observacoes.trim() || null,
      ...(notaId ? { documentoFiscalId: notaId } : {}),
      itens: itens.map(paraItemDeCriacao),
    };
    setEnviando(true);
    try {
      const resultado = await criarPedido(corpo);
      if (resultado.tipo !== 'ok') {
        setErroEnvio(resultado.message);
        return;
      }
      const pv = resultado.resultado;
      setSucesso(`PV ${pv.numero} criado.`);
      navegar(destinoAoConcluir(pv.id));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col">
      <div className="flex flex-col gap-6 p-6">
        <h1 className="text-xl font-semibold leading-tight">Novo pedido do cliente</h1>
        {carregamentoNota === 'carregando' && (
          <p role="status" className="text-sm text-muted-foreground">
            Carregando os itens da nota…
          </p>
        )}
        {carregamentoNota === 'erro' && <FormMessage>{MENSAGEM_FALHA_NOTA}</FormMessage>}
        {rascunhoNota && rascunhoNota.temItens && (
          <p className="text-sm text-muted-foreground">
            Itens pré-preenchidos da NF{rascunhoNota.documento.numero ? ` ${rascunhoNota.documento.numero}` : ''}. Revise antes de confirmar o PV.
          </p>
        )}
        {rascunhoNota && !rascunhoNota.temItens && <FormMessage>{COPIA_NOTA_SEM_ITENS}</FormMessage>}

        {pvsAbertosDaNota.length > 0 && (
          <AnexarPvCard pvsAbertos={pvsAbertosDaNota} valor={destinoAnexo} onChange={setDestinoAnexo} desabilitado={enviando} />
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-semibold uppercase tracking-wider">Cabeçalho</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-cliente">Cliente</Label>
              <ClienteCombobox id="pv-cliente" value={cliente} onChange={setCliente} invalido={!!erros.clienteId} />
              {erros.clienteId && <FormMessage>{erros.clienteId}</FormMessage>}
              {rascunhoNota && !rascunhoNota.cliente && rascunhoNota.documentoEmitente && !cliente && (
                <p className="text-sm text-muted-foreground">{copiaClienteNaoEncontrado(rascunhoNota.documentoEmitente)}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-numero-cliente">Nº do pedido do cliente</Label>
              <Input
                id="pv-numero-cliente"
                value={numeroPedidoCliente}
                onChange={(e) => setNumeroPedidoCliente(e.target.value)}
                aria-describedby="pv-numero-cliente-ajuda"
                aria-invalid={erros.numeroPedidoCliente ? true : undefined}
              />
              {erros.numeroPedidoCliente && <FormMessage>{erros.numeroPedidoCliente}</FormMessage>}
              <p id="pv-numero-cliente-ajuda" className="text-sm text-muted-foreground">
                Opcional. Também é buscável na lista.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-data">Data</Label>
              <DatePicker
                id="pv-data"
                value={data}
                onValueChange={(value) => setData(value)}
                aria-invalid={erros.data ? true : undefined}
              />
              {erros.data && <FormMessage>{erros.data}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-prazo">Prazo solicitado</Label>
              <DatePicker id="pv-prazo" value={prazo} onValueChange={(value) => setPrazo(value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-prioridade">Prioridade</Label>
              <CampoSelect
                id="pv-prioridade"
                value={String(prioridade)}
                onValueChange={(valor) => setPrioridade(Number(valor) as PrioridadePv)}
                options={PRIORIDADES.map((p) => ({ value: String(p.valor), label: p.rotulo }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-responsavel">Responsável</Label>
              <Input
                id="pv-responsavel"
                value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)}
                aria-invalid={erros.responsavelNome ? true : undefined}
              />
              {erros.responsavelNome && <FormMessage>{erros.responsavelNome}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pv-condicao">Condição comercial</Label>
              <Input
                id="pv-condicao"
                value={condicao}
                onChange={(e) => setCondicao(e.target.value)}
                aria-invalid={erros.condicaoComercial ? true : undefined}
              />
              {erros.condicaoComercial && <FormMessage>{erros.condicaoComercial}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <Label htmlFor="pv-observacoes">Observações</Label>
              <Textarea
                id="pv-observacoes"
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                aria-invalid={erros.observacoes ? true : undefined}
              />
              {erros.observacoes && <FormMessage>{erros.observacoes}</FormMessage>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider">Itens</CardTitle>
            <Button id="pv-adicionar-item" type="button" variant="outline" onClick={abrirNovoItem}>
              <Plus aria-hidden="true" />
              Adicionar item
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {itens.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <p className="text-center text-sm text-muted-foreground">Nenhum item neste PV.</p>
                <Button type="button" variant="outline" onClick={abrirNovoItem}>
                  <Plus aria-hidden="true" />
                  Adicionar item
                </Button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Item</th>
                    <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Material</th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Qtd.</th>
                    <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Un.</th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider">Preço</th>
                    <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Prazo</th>
                    <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Pendências</th>
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item, i) => {
                    const preco = item.precoUnitario ?? item.precoTabela ?? null;
                    return (
                      <tr key={`${item.codigoItemCliente}-${i}`} className="border-t">
                        <td className="max-w-[20rem] px-3 py-2">
                          <div className="truncate font-semibold" title={item.codigoItemCliente}>{item.codigoItemCliente}</div>
                          <div className="truncate text-muted-foreground">{item.descricao}</div>
                        </td>
                        <td className="max-w-[14rem] px-3 py-2">
                          {item.material ? (
                            <div className="truncate" title={item.material.nome}>
                              {item.material.codigo}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatarQuantidade(item.quantidade, item.unidade)}</td>
                        <td className="px-3 py-2">{item.unidade}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(preco)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatarData(item.previsaoEntrega ?? null)}</td>
                        <td className="px-3 py-2">
                          <PendenciasBadges pendencias={pendenciasDoRascunho(item)} />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button type="button" variant="ghost" size="sm" aria-label={`Editar item ${i + 1}`} onClick={() => editarItem(i)}>
                            <Pencil aria-hidden="true" />
                            Editar
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" variant="ghost" size="sm" aria-label={`Mais ações do item ${i + 1}`}>
                                <MoreHorizontal aria-hidden="true" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setItens((atuais) => atuais.filter((_, j) => j !== i))}>
                                <Trash2 aria-hidden="true" />
                                Remover item
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {erros.itens && <FormMessage>{erros.itens}</FormMessage>}
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 border-t bg-white px-6 py-3">
        {erroEnvio && <FormMessage className="mb-2">{erroEnvio}</FormMessage>}
        {sucesso && (
          <p role="status" className="mb-2 text-sm text-emerald-700">
            {sucesso}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navegar(notaId ? `/remessas?nota=${encodeURIComponent(notaId)}` : '/vendas/pedidos')} disabled={enviando}>
            Voltar
          </Button>
          <Button
            type="button"
            onClick={() => void confirmar()}
            disabled={enviando || carregamentoNota === 'carregando' || (rascunhoNota !== null && !rascunhoNota.temItens)}
          >
            {anexando ? 'Anexar itens ao pedido' : 'Criar pedido'}
          </Button>
        </div>
      </div>

      <PedidoItemSheet
        aberto={sheetAberto}
        onAbertoChange={setSheetAberto}
        cliente={cliente}
        inicial={indiceEdicao === null ? null : (itens[indiceEdicao] ?? null)}
        onSalvar={salvarItem}
      />
    </div>
  );
}
